import i18next, { type i18n as I18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import adminEn from './en/admin.json' with { type: 'json' };
import authEn from './en/auth.json' with { type: 'json' };
import commonEn from './en/common.json' with { type: 'json' };
import interactionsEn from './en/interactions.json' with { type: 'json' };
import softphoneEn from './en/softphone.json' with { type: 'json' };
import supervisionEn from './en/supervision.json' with { type: 'json' };
import ticketsEn from './en/tickets.json' with { type: 'json' };
import adminNe from './ne/admin.json' with { type: 'json' };
import authNe from './ne/auth.json' with { type: 'json' };
import commonNe from './ne/common.json' with { type: 'json' };
import interactionsNe from './ne/interactions.json' with { type: 'json' };
import softphoneNe from './ne/softphone.json' with { type: 'json' };
import supervisionNe from './ne/supervision.json' with { type: 'json' };
import ticketsNe from './ne/tickets.json' with { type: 'json' };

/** The languages the MVP ships. Nepali is a first-class language, not a later addition. */
export const languages = ['en', 'ne'] as const;
export type Language = (typeof languages)[number];

export const defaultNamespace = 'common';
export const namespaces = [
  'common',
  'auth',
  'admin',
  'softphone',
  'supervision',
  'interactions',
  'tickets',
] as const;

/** Where the chosen language is stored, so it survives a reload before the account loads. */
export const languageStorageKey = 'ccp.language';

const resources = {
  en: {
    common: commonEn,
    auth: authEn,
    admin: adminEn,
    softphone: softphoneEn,
    supervision: supervisionEn,
    interactions: interactionsEn,
    tickets: ticketsEn,
  },
  ne: {
    common: commonNe,
    auth: authNe,
    admin: adminNe,
    softphone: softphoneNe,
    supervision: supervisionNe,
    interactions: interactionsNe,
    tickets: ticketsNe,
  },
} as const;

/**
 * Bundles the translations rather than fetching them: the workspace is a static bundle, and a
 * runtime fetch would be a request a restricted deployment does not need to make.
 */
export function createI18n(language?: Language): I18n {
  const instance = i18next.createInstance();
  void instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      lng: language,
      fallbackLng: 'en',
      supportedLngs: [...languages],
      defaultNS: defaultNamespace,
      ns: [...namespaces],
      interpolation: { escapeValue: false },
      detection: {
        order: ['localStorage', 'navigator'],
        lookupLocalStorage: languageStorageKey,
        caches: ['localStorage'],
      },
      returnNull: false,
    });
  return instance;
}

/** Keeps `<html lang>` in step, which screen readers and hyphenation both depend on. */
export function applyDocumentLanguage(language: string): void {
  globalThis.document?.documentElement.setAttribute('lang', language);
}

export type TranslationResources = typeof resources;
